import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Banner, Button, Field } from '@aions/ui-primitives';
import { apiFetch, logout } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';
import { RecursoCard, type RecursoCardData } from '../components/RecursoCard';

interface ListResponse {
  cases: ReadonlyArray<RecursoCardData>;
}

type OrigemFilter = 'all' | 'denunciante' | 'denunciado_via_rh';

export function DashboardPage({
  onSelectCase,
  onLogout,
}: {
  onSelectCase: (id: string) => void;
  onLogout: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();
  const [cases, setCases] = useState<ReadonlyArray<RecursoCardData> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterOrigem, setFilterOrigem] = useState<OrigemFilter>('all');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch('/api/recurso/cases');
        if (cancelled) return;
        if (!resp.ok) {
          setError(t('common.error_unknown'));
          return;
        }
        const data = (await resp.json()) as ListResponse;
        setCases(data.cases);
      } catch {
        if (!cancelled) setError(t('common.error_network'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // i18n.t é estável; carrega uma vez no mount.
  }, []);

  const handleLogout = async (): Promise<void> => {
    await logout();
    setSession(null);
    onLogout();
  };

  const filtered = useMemo(() => {
    if (cases === null) return null;
    if (filterOrigem === 'all') return cases;
    return cases.filter((c) => c.recursoOrigem === filterOrigem);
  }, [cases, filterOrigem]);

  return (
    <div>
      <header className="dash-head">
        <h1 className="dash-head__title">{t('dashboard.title')}</h1>
        <div className="dash-head__actions">
          {session?.user.email && <span className="dash-head__user">{session.user.email}</span>}
          <Button variant="ghost" size="sm" onClick={() => void handleLogout()}>
            {t('dashboard.logout')}
          </Button>
        </div>
      </header>

      <Field label={t('dashboard.filter_origem')} className="dash-filter">
        {({ id }) => (
          <select
            id={id}
            className="cf-input"
            value={filterOrigem}
            onChange={(e) => setFilterOrigem(e.target.value as OrigemFilter)}
          >
            <option value="all">{t('dashboard.filter_all')}</option>
            <option value="denunciante">{t('dashboard.origem_denunciante')}</option>
            <option value="denunciado_via_rh">{t('dashboard.origem_denunciado_via_rh')}</option>
          </select>
        )}
      </Field>

      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {cases === null && !error && (
        // Skeleton no lugar do texto cru de carregando (role=status + aria-busy).
        <div className="case-grid" role="status" aria-busy="true" aria-label={t('app.loading')}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="cf-card cf-card--pad-md case-card case-card--skeleton"
              aria-hidden="true"
            >
              <span className="skeleton-line skeleton-line--title" />
              <span className="skeleton-line skeleton-line--id" />
              <span className="skeleton-line skeleton-line--badges" />
            </div>
          ))}
        </div>
      )}
      {filtered !== null && filtered.length === 0 && (
        <Banner tone="info" role="status">
          {t('dashboard.empty')}
        </Banner>
      )}

      {filtered !== null && filtered.length > 0 && (
        <div className="case-grid" role="list" aria-label={t('dashboard.title')}>
          {filtered.map((c) => (
            <div key={c.caseId} role="listitem">
              <RecursoCard data={c} onSelect={onSelectCase} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
